import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import CBICSearchTab from './CBICSearchTab';
import AppSidebar from '../components/AppSidebar';
import {
  Search,
  Send,
  Loader2,
  BookOpen,
  Clock,
  Home,
  FolderOpen,
  Users,
  BarChart3,
  LogOut,
  Shield,
  FileText,
  Download,
  Copy,
  ChevronRight,
  Scale,
  Sparkles,
  Plus,
  Trash2,
  ArrowRight,
  Link2,
  Library,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODEL_OPTIONS = [
  { id: 'o3', label: 'OpenAI o3', desc: 'Best for complex reasoning' },
  { id: 'claude-opus-4-6', label: 'Claude Opus 4.6', desc: 'Best for nuanced analysis' },
  { id: 'gpt-5.2', label: 'GPT-5.2', desc: 'Fast & reliable' },
  { id: 'o4-mini', label: 'OpenAI o4-mini', desc: 'Efficient reasoning' },
];


// Safely render LLM markdown output. ReactMarkdown does NOT execute raw HTML,
// so prompt-injection attempts to embed <script> tags are inert.
const FormattedResponse = ({ text }) => {
  if (!text) return null;
  const components = {
    h1: ({ children }) => <h1 className="text-2xl font-bold text-[#0F172A] mt-6 mb-4">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold text-[#1E3A8A] mt-8 mb-3 border-b-2 border-[#1E3A8A]/20 pb-2">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-bold text-slate-800 mt-6 mb-2 border-b border-slate-200 pb-1">{children}</h3>,
    p: ({ children }) => <p className="mb-3 text-slate-700 leading-relaxed">{children}</p>,
    strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
    em: ({ children }) => <em className="italic text-slate-700">{children}</em>,
    ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>,
    li: ({ children }) => <li className="text-slate-700 leading-relaxed">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#F59E0B] bg-amber-50/50 pl-4 py-2 my-3 text-slate-700 italic rounded-r-lg">
        {children}
      </blockquote>
    ),
    code: ({ inline, children }) =>
      inline ? (
        <code className="bg-slate-100 text-[#1E3A8A] px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
      ) : (
        <code className="block bg-slate-900 text-slate-100 p-3 rounded-lg text-sm font-mono overflow-x-auto my-3">{children}</code>
      ),
    hr: () => <hr className="my-4 border-slate-200" />,
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">
        {children}
      </a>
    ),
  };
  return (
    <div className="research-content">
      <ReactMarkdown components={components}>{text}</ReactMarkdown>
    </div>
  );
};

// Research History Item
const HistoryItem = ({ item, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-3 rounded-lg transition-all group ${
      isActive 
        ? 'bg-[#1E3A8A]/10 border border-[#1E3A8A]/20' 
        : 'hover:bg-slate-50 border border-transparent'
    }`}
    data-testid={`research-history-${item.research_id}`}
  >
    <p className={`text-sm font-medium line-clamp-2 ${isActive ? 'text-[#1E3A8A]' : 'text-slate-700'}`}>
      {item.query.substring(0, 100)}{item.query.length > 100 ? '...' : ''}
    </p>
    <div className="flex items-center gap-2 mt-1.5">
      <Clock className="w-3 h-3 text-slate-400" />
      <span className="text-xs text-slate-400">{new Date(item.created_at).toLocaleDateString()}</span>
      {item.linked_doc_id && <Link2 className="w-3 h-3 text-amber-500" />}
    </div>
  </button>
);

export default function ResearchPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ai'); // 'ai' | 'cbic'
  const [query, setQuery] = useState('');
  const [selectedModel, setSelectedModel] = useState('o3');
  const [isResearching, setIsResearching] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  // Sticky banner for LLM-budget-exhausted errors — persists across polls until the user dismisses
  const [budgetError, setBudgetError] = useState(null);
  const [history, setHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [linkedDocId, setLinkedDocId] = useState('');
  const [showDocPicker, setShowDocPicker] = useState(false);
  // Library context picker (up to 5 docs)
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [selectedLibDocs, setSelectedLibDocs] = useState([]); // [{lib_doc_id, filename, tags}]
  const [showLibPicker, setShowLibPicker] = useState(false);
  const [libPickerQuery, setLibPickerQuery] = useState('');
  const textareaRef = useRef(null);
  const resultRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/research/history`);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch research history:', err);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`);
      setDocuments(res.data.filter(d => d.status === 'text_extracted'));
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  }, []);

  const fetchLibraryDocs = useCallback(async (q = '') => {
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      const res = await axios.get(`${API}/library/list`, { params });
      // Only ready docs
      setLibraryDocs((res.data.items || []).filter((d) => d.status === 'ready'));
    } catch (err) {
      console.warn('Library docs fetch failed', err);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchDocuments();
    fetchLibraryDocs();
    // Pick up library docs added from the document-viewer page via sessionStorage
    try {
      const seed = JSON.parse(sessionStorage.getItem('research_lib_context') || '[]');
      if (Array.isArray(seed) && seed.length > 0) {
        setSelectedLibDocs((prev) => {
          const seen = new Set(prev.map((d) => d.lib_doc_id));
          const merged = [...prev];
          for (const s of seed) {
            if (!seen.has(s.lib_doc_id) && merged.length < 5) merged.push(s);
          }
          return merged;
        });
        sessionStorage.removeItem('research_lib_context');
        toast.success(`Added ${seed.length} library doc${seed.length !== 1 ? 's' : ''} to context`);
      }
    } catch (e) {
      // sessionStorage may be unavailable (private browsing, etc.) — non-fatal
      console.warn('sessionStorage read for research_lib_context failed:', e);
    }
  }, [fetchHistory, fetchDocuments, fetchLibraryDocs]);

  // Refresh library list when picker is opened or query changes
  useEffect(() => {
    if (showLibPicker) {
      fetchLibraryDocs(libPickerQuery);
    }
  }, [showLibPicker, libPickerQuery, fetchLibraryDocs]);

  const toggleLibDoc = (doc) => {
    setSelectedLibDocs((prev) => {
      const already = prev.find((d) => d.lib_doc_id === doc.lib_doc_id);
      if (already) return prev.filter((d) => d.lib_doc_id !== doc.lib_doc_id);
      if (prev.length >= 5) {
        toast.info('Maximum 5 library documents can be selected as context');
        return prev;
      }
      return [...prev, { lib_doc_id: doc.lib_doc_id, filename: doc.filename, tags: doc.tags }];
    });
  };

  const handleSubmit = async () => {
    if (!query.trim() || isResearching) return;
    setIsResearching(true);
    setCurrentResult(null);
    
    try {
      // Submit query — returns immediately with research_id
      const submitRes = await axios.post(`${API}/research/query`, {
        query: query.trim(),
        model: selectedModel,
        linked_doc_id: linkedDocId || null,
        library_doc_ids: selectedLibDocs.map((d) => d.lib_doc_id),
      }, { timeout: 30000 });
      
      const researchId = submitRes.data.research_id;
      setQuery('');
      setLinkedDocId('');
      setShowDocPicker(false);
      fetchHistory();

      // Poll for completion
      const pollInterval = 3000; // 3 seconds
      const maxPolls = 120; // 6 minutes max
      let polls = 0;

      const poll = async () => {
        try {
          const res = await axios.get(`${API}/research/${researchId}`);
          if (res.data.status === 'completed') {
            setCurrentResult(res.data);
            setIsResearching(false);
            fetchHistory();
            toast.success('Research complete!');
            if (resultRef.current) {
              resultRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          } else if (res.data.status === 'failed') {
            setIsResearching(false);
            const msg = res.data.error_message || 'Research failed. Please try again.';
            // A budget-exhausted error deserves a sticky, obvious banner — not a fleeting toast.
            if (msg.toLowerCase().includes('budget exhausted') || msg.toLowerCase().includes('universal key')) {
              setBudgetError(msg);
            }
            toast.error(msg, { duration: 10000 });
          } else {
            polls++;
            if (polls < maxPolls) {
              setTimeout(poll, pollInterval);
            } else {
              setIsResearching(false);
              toast.error('Research is taking too long. Check history later.');
            }
          }
        } catch (err) {
          polls++;
          if (polls < maxPolls) {
            setTimeout(poll, pollInterval);
          } else {
            setIsResearching(false);
            toast.error('Lost connection. Check history for results.');
          }
        }
      };
      
      setTimeout(poll, pollInterval);
      
    } catch (err) {
      // Surface the MOST-specific reason we know. Users on production see this
      // toast; the raw axios error shape is our only clue to why the request
      // failed before hitting the LLM background task.
      let detail;
      if (err.response?.data?.detail) {
        // Backend returned a proper JSON error — use it verbatim
        detail = err.response.data.detail;
      } else if (err.response) {
        // Backend responded but not with the expected JSON shape (e.g., 502 HTML)
        detail = `Server responded ${err.response.status} ${err.response.statusText || ''}. Please retry — if it keeps happening, share this status code.`;
      } else if (err.code === 'ECONNABORTED') {
        detail = 'Request timed out after 30 s reaching the server. Please check your internet and retry.';
      } else if (err.request) {
        // Request was sent but no response received (CORS, DNS, offline, ingress unreachable)
        detail = `Could not reach the server (${err.message || 'network error'}). Please check your internet connection and retry. If it persists on production, the deployed backend may be down — try redeploying from the Emergent app.`;
      } else {
        detail = err.message || 'Failed to submit research. Please try again.';
      }
      console.error('Research submission failed:', err); // dev-tools debugging aid
      if (detail.toLowerCase().includes('budget exhausted') || detail.toLowerCase().includes('universal key')) {
        setBudgetError(detail);
      }
      toast.error(detail, { duration: 10000 });
      setIsResearching(false);
    }
  };

  const loadHistoryItem = async (researchId) => {
    try {
      const res = await axios.get(`${API}/research/${researchId}`);
      setCurrentResult(res.data);
    } catch (err) {
      toast.error('Failed to load research result');
    }
  };

  const deleteHistoryItem = async (e, researchId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/research/${researchId}`);
      setHistory(prev => prev.filter(h => h.research_id !== researchId));
      if (currentResult?.research_id === researchId) setCurrentResult(null);
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleCopy = () => {
    if (currentResult?.response) {
      navigator.clipboard.writeText(currentResult.response);
      toast.success('Copied to clipboard');
    }
  };

  const handleDownloadWord = async () => {
    if (!currentResult) return;
    try {
      const res = await axios.post(`${API}/research/${currentResult.research_id}/export`, {}, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Research_${currentResult.research_id}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.warn('Logout API call failed (proceeding with client-side cleanup):', e);
    }
    navigate('/');
  };

  const linkedDoc = documents.find(d => d.doc_id === linkedDocId);

  return (
    <div className="min-h-screen bg-slate-50/50">
      <AppSidebar user={user} onLogout={handleLogout} active="research" />
      
      <main className="ml-72 min-h-screen">
        {/* Persistent LLM budget-exhausted banner. Only shown when the last
            research call failed with a budget error — action is on the user
            (top up the Emergent Universal Key). Dismissable so it doesn't
            block the UI once the user has seen it. */}
        {budgetError && (
          <div
            className="bg-red-50 border-b-2 border-red-300 px-6 py-3 flex items-start gap-3"
            data-testid="budget-error-banner"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">LLM budget exhausted</p>
              <p className="text-xs text-red-800 mt-0.5">{budgetError}</p>
              <p className="text-xs text-red-700 mt-1">
                <strong>How to fix:</strong> open the Emergent app → click your profile avatar → <em>Universal Key</em> → <em>Add Balance</em> (or enable auto top-up). Retry the query once the key is topped up.
              </p>
            </div>
            <button
              onClick={() => setBudgetError(null)}
              className="text-red-600 hover:text-red-800 text-xs font-medium underline flex-shrink-0"
              data-testid="dismiss-budget-error"
            >
              Dismiss
            </button>
          </div>
        )}
        <div className="flex h-screen">
          {/* History Panel (only for AI Research tab) */}
          {activeTab === 'ai' && (
          <div className="w-80 border-r border-slate-200 bg-white flex flex-col">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-bold text-slate-800">Research History</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setCurrentResult(null); setQuery(''); }}
                  className="text-[#1E3A8A] hover:bg-[#1E3A8A]/10"
                  data-testid="new-research-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />New
                </Button>
              </div>
              <p className="text-xs text-slate-500">{history.length} past queries</p>
            </div>
            <ScrollArea className="flex-1 p-3">
              {history.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No research yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {history.map((item) => (
                    <div key={item.research_id} className="relative group">
                      <HistoryItem
                        item={item}
                        isActive={currentResult?.research_id === item.research_id}
                        onClick={() => loadHistoryItem(item.research_id)}
                      />
                      <button
                        onClick={(e) => deleteHistoryItem(e, item.research_id)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50"
                        data-testid={`delete-research-${item.research_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          )}

          {/* Main Research Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tab Switcher */}
            <div className="border-b border-slate-200 bg-white px-5 pt-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setActiveTab('ai')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
                    activeTab === 'ai'
                      ? 'border-[#1E3A8A] text-[#1E3A8A]'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="tab-ai-research"
                >
                  <Sparkles className="w-4 h-4" />
                  AI Legal Research
                </button>
                <button
                  onClick={() => setActiveTab('cbic')}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
                    activeTab === 'cbic'
                      ? 'border-[#1E3A8A] text-[#1E3A8A]'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="tab-cbic-search"
                >
                  <Library className="w-4 h-4" />
                  CBIC Circular Search
                </button>
              </div>
            </div>

            {activeTab === 'cbic' ? (
              <CBICSearchTab />
            ) : (
            <>
            {/* Result Area */}
            <ScrollArea className="flex-1 p-8">
              {currentResult ? (
                <div ref={resultRef} className="max-w-4xl mx-auto">
                  {/* Result Header */}
                  <div className="mb-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Scale className="w-5 h-5 text-[#1E3A8A]" />
                          <span className="text-xs font-medium bg-[#1E3A8A]/10 text-[#1E3A8A] px-2.5 py-1 rounded-full">
                            {MODEL_OPTIONS.find(m => m.id === currentResult.model)?.label || currentResult.model}
                          </span>
                          {currentResult.linked_doc_filename && (
                            <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                              <Link2 className="w-3 h-3" />{currentResult.linked_doc_filename}
                            </span>
                          )}
                        </div>
                        <h2 className="text-base font-semibold text-slate-600 leading-relaxed">
                          {currentResult.query}
                        </h2>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={handleCopy} className="border-slate-200" data-testid="copy-research-btn">
                          <Copy className="w-4 h-4 mr-1.5" />Copy
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleDownloadWord} className="border-slate-200" data-testid="download-research-btn">
                          <Download className="w-4 h-4 mr-1.5" />Word
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Result Body */}
                  <Card className="border-0 shadow-sm bg-white">
                    <CardContent className="p-8">
                      <FormattedResponse text={currentResult.response} />
                    </CardContent>
                  </Card>

                  {currentResult.processing_time && (
                    <p className="text-xs text-slate-400 mt-4 text-right">
                      Processed in {currentResult.processing_time}s
                    </p>
                  )}
                </div>
              ) : !isResearching ? (
                <div className="max-w-2xl mx-auto text-center pt-20">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-2xl flex items-center justify-center shadow-lg">
                    <Search className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">Legal Research Assistant</h2>
                  <p className="text-slate-500 mb-8 max-w-lg mx-auto">
                    Ask complex legal questions and get detailed, citation-backed analysis powered by advanced AI reasoning models.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-lg mx-auto">
                    {[
                      'Penalty provisions under GST Section 74',
                      'Legal position on pre-deposit requirements',
                      'Time limitation for SCN issuance',
                      'Appellate remedies under Customs Act'
                    ].map((example, i) => (
                      <button
                        key={i}
                        onClick={() => setQuery(example)}
                        className="p-3 rounded-xl border border-slate-200 hover:border-[#1E3A8A]/30 hover:bg-[#1E3A8A]/5 transition-all text-left group"
                        data-testid={`example-query-${i}`}
                      >
                        <p className="text-sm text-slate-600 group-hover:text-[#1E3A8A]">{example}</p>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#1E3A8A] mt-1" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto text-center pt-32">
                  <div className="w-16 h-16 mx-auto mb-6 relative">
                    <div className="absolute inset-0 border-4 border-[#1E3A8A]/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin"></div>
                    <Sparkles className="w-6 h-6 text-[#F59E0B] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Researching your query...</h3>
                  <p className="text-sm text-slate-500">Using {MODEL_OPTIONS.find(m => m.id === selectedModel)?.label} for deep legal analysis. This may take a minute.</p>
                </div>
              )}
            </ScrollArea>

            {/* Input Area — Always at bottom */}
            <div className="border-t border-slate-200 bg-white p-5">
              <div className="max-w-4xl mx-auto">
                {/* Model selector + Document link */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg p-1">
                    {MODEL_OPTIONS.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setSelectedModel(m.id)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                          selectedModel === m.id
                            ? 'bg-[#1E3A8A] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                        title={m.desc}
                        data-testid={`model-${m.id}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex-1" />

                  {/* Link Document */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDocPicker(!showDocPicker)}
                      className={`text-xs ${linkedDocId ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200'}`}
                      data-testid="link-document-btn"
                    >
                      <Link2 className="w-3.5 h-3.5 mr-1.5" />
                      {linkedDoc ? linkedDoc.filename.substring(0, 25) + (linkedDoc.filename.length > 25 ? '...' : '') : 'Link Notice'}
                    </Button>
                    
                    {showDocPicker && (
                      <div className="absolute bottom-full right-0 mb-2 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-600">Link a Notice</span>
                          {linkedDocId && (
                            <button onClick={() => { setLinkedDocId(''); setShowDocPicker(false); }} className="text-xs text-red-500 hover:underline">Clear</button>
                          )}
                        </div>
                        <ScrollArea className="max-h-48">
                          {documents.length === 0 ? (
                            <p className="p-4 text-xs text-slate-400 text-center">No processed documents found</p>
                          ) : (
                            documents.map((doc) => (
                              <button
                                key={doc.doc_id}
                                onClick={() => { setLinkedDocId(doc.doc_id); setShowDocPicker(false); }}
                                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2 transition-colors ${
                                  linkedDocId === doc.doc_id ? 'bg-amber-50' : ''
                                }`}
                                data-testid={`pick-doc-${doc.doc_id}`}
                              >
                                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-700 truncate">{doc.filename}</p>
                                  <p className="text-xs text-slate-400">{doc.page_count} pages</p>
                                </div>
                                {linkedDocId === doc.doc_id && <ChevronRight className="w-4 h-4 text-amber-500" />}
                              </button>
                            ))
                          )}
                        </ScrollArea>
                      </div>
                    )}
                  </div>

                  {/* Library Context Picker */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLibPicker(!showLibPicker)}
                      className={`text-xs ${selectedLibDocs.length > 0 ? 'border-[#1E3A8A]/50 bg-[#1E3A8A]/5 text-[#1E3A8A]' : 'border-slate-200'}`}
                      data-testid="library-context-btn"
                    >
                      <Library className="w-3.5 h-3.5 mr-1.5" />
                      {selectedLibDocs.length > 0
                        ? `${selectedLibDocs.length} Library Doc${selectedLibDocs.length > 1 ? 's' : ''}`
                        : 'Library Context'}
                    </Button>

                    {showLibPicker && (
                      <div className="absolute bottom-full right-0 mb-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="library-picker-popover">
                        <div className="p-3 border-b border-slate-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-slate-600">Pick up to 5 library documents</span>
                            {selectedLibDocs.length > 0 && (
                              <button onClick={() => setSelectedLibDocs([])} className="text-xs text-red-500 hover:underline" data-testid="library-picker-clear">
                                Clear ({selectedLibDocs.length})
                              </button>
                            )}
                          </div>
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              value={libPickerQuery}
                              onChange={(e) => setLibPickerQuery(e.target.value)}
                              placeholder="Search by filename..."
                              className="w-full pl-7 pr-2 h-8 rounded-lg border border-slate-200 text-xs bg-slate-50/50 focus:outline-none focus:border-[#1E3A8A]"
                              data-testid="library-picker-search"
                            />
                          </div>
                        </div>
                        <ScrollArea className="h-64">
                          {libraryDocs.length === 0 ? (
                            <p className="p-4 text-xs text-slate-400 text-center">No library documents found</p>
                          ) : (
                            libraryDocs.map((doc) => {
                              const isSelected = selectedLibDocs.some(s => s.lib_doc_id === doc.lib_doc_id);
                              return (
                                <button
                                  key={doc.lib_doc_id}
                                  onClick={() => toggleLibDoc(doc)}
                                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2 transition-colors ${
                                    isSelected ? 'bg-[#1E3A8A]/5' : ''
                                  }`}
                                  data-testid={`pick-lib-doc-${doc.lib_doc_id}`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    isSelected ? 'bg-[#1E3A8A] border-[#1E3A8A]' : 'border-slate-300'
                                  }`}>
                                    {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-700 truncate">{doc.filename}</p>
                                    {doc.tags?.length > 0 && (
                                      <p className="text-[10px] text-slate-400 truncate">{doc.tags.slice(0, 3).join(' · ')}</p>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected library docs as chips */}
                {selectedLibDocs.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="library-context-chips">
                    {selectedLibDocs.map((d) => (
                      <span
                        key={d.lib_doc_id}
                        className="inline-flex items-center gap-1 bg-[#1E3A8A]/10 text-[#1E3A8A] px-2 py-0.5 rounded-full text-[11px]"
                      >
                        <Library className="w-3 h-3" />
                        <span className="max-w-[180px] truncate">{d.filename}</span>
                        <button
                          onClick={() => toggleLibDoc(d)}
                          className="hover:bg-[#1E3A8A]/20 rounded-full ml-1 -mr-1 px-0.5"
                          data-testid={`remove-lib-chip-${d.lib_doc_id}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Query Input */}
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a legal question... e.g., 'What is the legal position on penalty under Section 74 when tax and interest are paid before SCN?'"
                    className="w-full min-h-[80px] max-h-[200px] p-4 pr-14 rounded-xl border border-slate-200 focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none resize-y text-sm text-slate-700 placeholder:text-slate-400 bg-slate-50/50"
                    disabled={isResearching}
                    data-testid="research-input"
                  />
                  <Button
                    onClick={handleSubmit}
                    disabled={!query.trim() || isResearching}
                    className="absolute bottom-3 right-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg px-4 h-9"
                    data-testid="research-submit-btn"
                  >
                    {isResearching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-1.5" />Research
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1.5 ml-1">Ctrl+Enter to submit</p>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
